import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import {
    User,
    Camera,
    Save,
    CreditCard,
    Crown,
    ExternalLink,
    Settings,
    Newspaper,
    Loader2,
    AlertCircle,
    CheckCircle,
} from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../redux/store';
import { userSelectOneObjectByQuery, DBUserThunks } from '../../redux/thunks/DBServiceThunks';
import { uploadProfilePicture, useCreateCustomerPortalSessionMutation } from '../../common/backend-api/backend-api';
import { updateUser } from '../../redux/slices/userSlice';
import { useSearchParams } from 'react-router-dom';
import PricingPlans from '../subscription/PricingPlans';
import CheckoutSessionModal from '../subscription/CheckoutSessionModal';
import CancelledSubscriptions from '../subscription/CancelledSubscriptions';
import {
    fetchSubscriptionDataIfNeededThunk,
    refreshSubscriptionDataThunk,
    fetchCheckoutSessionThunk
} from '../../redux/thunks/SubscriptionThunks';
import {
    selectSubscriptionLoading,
    selectShowPlans,
    selectActiveSubscription,
    selectCancelledSubscriptions,
    selectHasCancelledSubscriptions
} from '../../redux/selectors/SubscriptionSelectors';
import { setShowPlans } from '../../redux/slices/subscriptionSlice';
import JurisdictionSelect from '../common/JurisdictionSelect';
import { DEFAULT_JURISDICTION, JurisdictionCode } from '../../common/jurisdictions';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';

const AccountSettings: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isRTL = getLanguageDirection(i18n.language) === 'rtl';
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    const user = useAppSelector(state => {
        const authUserId = state.UserReducer.user?._id;
        return authUserId
            ? userSelectOneObjectByQuery(state.DBUserReducer, { _id: authUserId })
            : null;
    });

    const [displayName, setDisplayName] = useState(user?.displayName || '');
    const [profilePicture, setProfilePicture] = useState<File | null>(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [jurisdiction, setJurisdiction] = useState<JurisdictionCode | undefined>(user?.jurisdiction || DEFAULT_JURISDICTION);
    const [showLegalNewsFeed, setShowLegalNewsFeed] = useState<boolean>(user?.showLegalNewsFeed ?? false);
    const [legalNewsJurisdiction, setLegalNewsJurisdiction] = useState<JurisdictionCode>(user?.legalNewsJurisdiction || 'CYPRUS');

    const [sessionModalOpen, setSessionModalOpen] = useState(false);
    const [sessionInfo, setSessionInfo] = useState<any>(null);
    const [sessionLoading, setSessionLoading] = useState(false);

    const subscriptionLoading = useAppSelector(selectSubscriptionLoading);
    const showPlans = useAppSelector(selectShowPlans);
    const activeSubscription = useAppSelector(selectActiveSubscription);
    const cancelledSubscriptions = useAppSelector(selectCancelledSubscriptions);
    const hasCancelledSubscriptions = useAppSelector(selectHasCancelledSubscriptions);

    const [createCustomerPortalSession] = useCreateCustomerPortalSessionMutation();

    useEffect(() => {
        dispatch(fetchSubscriptionDataIfNeededThunk());

        const subscriptionParam = searchParams.get('subscription');
        if (subscriptionParam === 'success') {
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 5000);
            setTimeout(() => { dispatch(refreshSubscriptionDataThunk()); }, 1000);
        }

        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            fetchSessionInfo(sessionId);
        }
    }, [dispatch, searchParams]);

    const fetchSessionInfo = async (sessionId: string) => {
        try {
            setSessionLoading(true);
            const result = await dispatch(fetchCheckoutSessionThunk(sessionId)).unwrap();
            setSessionInfo(result);
            setSessionModalOpen(true);
        } catch (error) {
            console.error('Failed to fetch session info:', error);
            setError(t('errors.subscription.failedToLoadSession', 'Failed to load checkout session information'));
        } finally {
            setSessionLoading(false);
        }
    };

    const formatDate = (timestamp: number) => new Date(timestamp * 1000).toLocaleDateString();

    const handleProfilePictureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setProfilePicture(file);
            const reader = new FileReader();
            reader.onload = (e) => { setProfilePicturePreview(e.target?.result as string); };
            reader.readAsDataURL(file);
        }
    };

    const getUserInitials = () => {
        if (!displayName && !user?.displayName) return null;
        const name = displayName || user?.displayName || '';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    const renderAvatarContent = () => {
        if (profilePicturePreview) {
            return <img src={profilePicturePreview} alt={t('common.labels.profile')} className="w-full h-full object-cover rounded-full" />;
        }
        if (user?.profilePicture) {
            return <img src={user.profilePicture} alt={t('common.labels.profile')} className="w-full h-full object-cover rounded-full" />;
        }
        const initials = getUserInitials();
        if (initials) return <span className="text-2xl font-bold">{initials}</span>;
        return <User size={32} />;
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        setSaveSuccess(false);

        try {
            let profilePictureUrl = user?.profilePicture;

            if (profilePicture && profilePicturePreview) {
                const uploadResult = await dispatch(uploadProfilePicture.initiate({ imageData: profilePicturePreview })).unwrap();
                if (uploadResult.success && uploadResult.publicUrl) {
                    profilePictureUrl = uploadResult.publicUrl;
                } else {
                    throw new Error(uploadResult.error || t('errors.profile.failedToUploadPicture', 'Failed to upload profile picture'));
                }
            }

            const updateData: any = {};
            let hasChanges = false;

            if (displayName !== user?.displayName) { updateData.displayName = displayName; hasChanges = true; }
            if (profilePictureUrl !== user?.profilePicture) { updateData.profilePicture = profilePictureUrl; hasChanges = true; }
            if ((jurisdiction || DEFAULT_JURISDICTION) !== (user?.jurisdiction || DEFAULT_JURISDICTION)) { updateData.jurisdiction = jurisdiction || DEFAULT_JURISDICTION; hasChanges = true; }
            if (showLegalNewsFeed !== (user?.showLegalNewsFeed ?? false)) { updateData.showLegalNewsFeed = showLegalNewsFeed; hasChanges = true; }
            if (legalNewsJurisdiction !== (user?.legalNewsJurisdiction || 'CYPRUS')) { updateData.legalNewsJurisdiction = legalNewsJurisdiction; hasChanges = true; }

            if (hasChanges) {
                await dispatch(DBUserThunks.patch({ entity: { _id: user._id, ...updateData }, options: {} })).unwrap();
                dispatch(updateUser(updateData));
            }

            setProfilePicture(null);
            setProfilePicturePreview(null);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to save changes. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        try {
            setLoading(true);
            setError(null);
            const returnUrl = window.location.href;
            const result = await createCustomerPortalSession({ returnUrl }).unwrap();
            if (result.success && result.portalUrl) {
                window.location.href = result.portalUrl;
            } else {
                throw new Error(t('errors.subscription.failedToCreatePortalSession', 'Failed to create customer portal session'));
            }
        } catch (error: any) {
            console.error('Error opening customer portal:', error);
            setError(error.message || 'Failed to open subscription management. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const hasFormChanges = displayName !== (user?.displayName || '')
        || profilePicture !== null
        || (jurisdiction || DEFAULT_JURISDICTION) !== (user?.jurisdiction || DEFAULT_JURISDICTION)
        || showLegalNewsFeed !== (user?.showLegalNewsFeed ?? false)
        || legalNewsJurisdiction !== (user?.legalNewsJurisdiction || 'CYPRUS');

    const getActivePlanName = () => {
        if (!activeSubscription) return null;
        const item = activeSubscription.items?.data[0];
        if (item?.price?.product && typeof item.price.product === 'object') {
            return item.price.product.name || t('common.subscription.defaultPlanName', 'Subscription Plan');
        }
        return item?.price?.nickname || item?.plan?.nickname || activeSubscription.plan?.nickname || t('common.accountSettings.unknown');
    };

    return (
        <div
            dir={getLanguageDirection(i18n.language)}
            className="max-w-3xl mx-auto p-6 space-y-6"
        >
            {/* Header */}
            <div className="flex items-center gap-3">
                <Settings size={24} className="text-gold shrink-0" />
                <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                    {t('common.accountSettings.title')}
                </h1>
            </div>

            {/* Loading bar */}
            {loading && (
                <div className="w-full h-1 bg-secondary overflow-hidden rounded-full">
                    <div className="h-full bg-gold animate-pulse w-1/2 rounded-full" />
                </div>
            )}

            {/* Success alert */}
            {saveSuccess && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800">
                    <CheckCircle className="h-5 w-5 shrink-0 mt-0.5" />
                    <p className="text-sm font-medium">
                        {searchParams.get('subscription') === 'success'
                            ? t('notifications.subscription.activatedSuccess', 'Subscription activated successfully! Welcome to OmniLaw Pro!')
                            : t('notifications.profile.changesSaved', 'Changes saved successfully!')}
                    </p>
                </div>
            )}

            {/* Error alert */}
            {error && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/5 border border-destructive/20 text-destructive">
                    <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}

            {/* Profile Section */}
            <Card className="border-border bg-card">
                <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
                        <User size={18} className="text-gold" />
                        {t('common.accountSettings.profileInformation')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                    <div className={cn("flex gap-6", isRTL ? "flex-row-reverse" : "flex-row", "flex-col sm:flex-row")}>
                        {/* Avatar */}
                        <div className="flex flex-col items-center gap-2 shrink-0">
                            <div className="relative">
                                <div className="w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center overflow-hidden border-2 border-gold/30">
                                    {renderAvatarContent()}
                                </div>
                                <label className="absolute bottom-0 end-0 w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center cursor-pointer hover:bg-gold-bright transition-colors shadow-md">
                                    <Camera size={14} />
                                    <input hidden accept="image/*" type="file" onChange={handleProfilePictureChange} />
                                </label>
                            </div>
                            <p className="text-xs text-muted-foreground">{t('common.accountSettings.clickCameraToChange')}</p>
                        </div>

                        {/* Fields */}
                        <div className="flex-1 space-y-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="displayName">{t('common.labels.displayName')}</Label>
                                <Input
                                    id="displayName"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    disabled={loading}
                                    className="bg-background border-border focus:ring-gold focus:border-gold"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <Label>{t('common.jurisdiction.default')}</Label>
                                <JurisdictionSelect
                                    value={jurisdiction}
                                    onChange={setJurisdiction}
                                    label={t('common.jurisdiction.default')}
                                    size="small"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="email">{t('common.labels.email')}</Label>
                                <Input
                                    id="email"
                                    value={user?.email || ''}
                                    disabled
                                    className="bg-secondary/50 border-border text-muted-foreground"
                                />
                                <p className="text-xs text-muted-foreground">{t('common.accountSettings.emailCannotBeChanged', 'Email cannot be changed')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Save */}
                    <div className="flex justify-end pt-2">
                        <Button
                            onClick={handleSave}
                            disabled={!hasFormChanges || loading}
                            className="gap-2 bg-gold hover:bg-gold-bright text-white border-none min-w-28"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={15} />}
                            {loading ? t('common.actions.saving', 'Saving...') : t('common.actions.saveChanges', 'Save Changes')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Legal News Feed Section */}
            <Card className="border-border bg-card">
                <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
                        <Newspaper size={18} className="text-gold" />
                        {t('common.accountSettings.legalNewsFeed', 'Legal News Feed')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                        <Switch
                            id="showLegalNews"
                            checked={showLegalNewsFeed}
                            onCheckedChange={setShowLegalNewsFeed}
                            disabled={loading}
                            className="data-[state=checked]:bg-gold"
                        />
                        <Label htmlFor="showLegalNews" className="cursor-pointer text-sm text-foreground">
                            {t('common.accountSettings.showLegalNewsFeed', 'Show legal news and announcements when no case is open')}
                        </Label>
                    </div>

                    {showLegalNewsFeed && (
                        <div className="ps-12 max-w-xs">
                            <Label className="text-xs text-muted-foreground mb-1.5 block">
                                {t('common.accountSettings.newsJurisdiction', 'News Jurisdiction')}
                            </Label>
                            <select
                                value={legalNewsJurisdiction}
                                onChange={(e) => setLegalNewsJurisdiction(e.target.value as JurisdictionCode)}
                                disabled={loading}
                                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold disabled:opacity-50"
                            >
                                <option value="CYPRUS">{t('common.jurisdiction.cyprus', 'Cyprus')}</option>
                                <option value="AUSTRIA" disabled>{t('common.jurisdiction.austria', 'Austria')} - {t('common.comingSoon', 'Coming Soon')}</option>
                            </select>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Subscription Section */}
            <Card className="border-border bg-card" dir={getLanguageDirection(i18n.language)}>
                <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
                        <CreditCard size={18} className="text-gold" />
                        {t('common.accountSettings.subscription')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {subscriptionLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-gold" />
                        </div>
                    ) : showPlans ? (
                        <div>
                            <p className="text-sm text-muted-foreground mb-4">
                                {t('common.accountSettings.noActiveSubscription')}
                            </p>
                            {!subscriptionLoading && (
                                <PricingPlans
                                    userId={user?._id?.toString()}
                                    userEmail={user?.email}
                                />
                            )}
                        </div>
                    ) : activeSubscription ? (
                        <div className="space-y-4">
                            {/* Active plan card */}
                            <div className="p-4 rounded-xl bg-gold/5 border border-gold/20">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Crown size={20} className="text-gold" />
                                        <span className="font-semibold text-gold" style={{ fontFamily: 'var(--font-display)' }}>
                                            {getActivePlanName()}
                                        </span>
                                    </div>
                                    <span className={cn(
                                        "text-sm font-medium px-2.5 py-0.5 rounded-full",
                                        activeSubscription.status === 'active'
                                            ? "bg-green-100 text-green-700"
                                            : "bg-amber-100 text-amber-700"
                                    )}>
                                        {activeSubscription.status.charAt(0).toUpperCase() + activeSubscription.status.slice(1)}
                                    </span>
                                </div>

                                {(activeSubscription.current_period_end || activeSubscription.cancel_at_period_end) && (
                                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                                        {activeSubscription.current_period_end && (
                                            <span>{t('common.accountSettings.nextBilling')}: {formatDate(activeSubscription.current_period_end)}</span>
                                        )}
                                        {activeSubscription.cancel_at_period_end && (
                                            <span className="text-amber-600">{t('common.accountSettings.cancelsAtPeriodEnd')}</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-border" />

                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-foreground mb-1">{t('common.accountSettings.manageSubscription')}</h3>
                                    <p className="text-sm text-muted-foreground">{t('common.accountSettings.updateBilling')}</p>
                                </div>
                                <Button
                                    variant="outline"
                                    onClick={handleManageSubscription}
                                    className="gap-2 border-border hover:border-gold hover:text-gold"
                                >
                                    <CreditCard size={15} />
                                    {t('common.accountSettings.manage')}
                                    <ExternalLink size={14} />
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">
                                {t('common.accountSettings.noActiveSubscriptionFound')}
                            </p>
                            <Button
                                onClick={() => dispatch(setShowPlans(true))}
                                className="bg-gold hover:bg-gold-bright text-white border-none gap-2"
                            >
                                <Crown size={15} />
                                {t('common.accountSettings.viewPlans')}
                            </Button>
                        </div>
                    )}

                    {hasCancelledSubscriptions && (
                        <div className="mt-4">
                            <CancelledSubscriptions subscriptions={cancelledSubscriptions} />
                        </div>
                    )}
                </CardContent>
            </Card>

            <CheckoutSessionModal
                open={sessionModalOpen}
                onClose={() => setSessionModalOpen(false)}
                sessionInfo={sessionInfo}
                loading={sessionLoading}
            />
        </div>
    );
};

export default AccountSettings;
